import { Box, Skeleton } from '@chakra-ui/react';
import React from 'react';
import { Navigate } from 'react-router';
import { useParams } from 'react-router-dom';
import { z } from 'zod';

import { ReviewLoanEditView } from 'packages/loans/ui/edits/ReviewLoanEditView';
import { RouteErrorBoundary } from '~/contexts/MonitoringProvider';
import { dashboardRoutePadding } from '~/packages/common-frontend/constants';
import { FundingRequestTemplateProviderV1 } from '~/packages/funding-requests/hooks/useFundingRequestTemplateContextV1';
import { useGetResolvedLoanByIdQuery } from '~/packages/loans/requests/loans.gql.generated';

// Types
const RouteParamsStruct = z.object({
  loanId: z.string({ message: 'Missing URL parameter' }),
});

const RouteBase = () => {
  const params = useParams();
  const { loanId } = RouteParamsStruct.parse(params);

  const { data, loading, error } = useGetResolvedLoanByIdQuery({
    fetchPolicy: 'network-only',
    variables: { loanId },
  });
  if (error) throw error;

  if (!data || loading) {
    return <Skeleton height="600px" />;
  }

  const { facility, funding } = data.loan;

  if (
    !data.loan.edits.destination ||
    data.loan.edits.destination.status !== 'pending'
  ) {
    throw new Error(
      'No loan edits have been submitted for this loan. If you would like to review this loan, please submit a loan edit.',
    );
  }

  return (
    <FundingRequestTemplateProviderV1
      mode="edit"
      allowedActions="readOnly"
      creditFacilityId={facility.creditFacilityId}
      // TODO: @jendevelops @markhaghani - update to support multiple funding requests after providers work
      fundingRequestId={funding.requests[0].fundingRequestId}
      loanId={loanId}
    >
      <Box padding={dashboardRoutePadding}>
        <ReviewLoanEditView />
      </Box>
    </FundingRequestTemplateProviderV1>
  );
};

export const Route = () => {
  return (
    <RouteErrorBoundary
      fallback={({ message, showErrorToast }) => {
        showErrorToast(message);
        return <Navigate to="/loans" />;
      }}
    >
      <RouteBase />
    </RouteErrorBoundary>
  );
};
